import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Globe, Crown } from "lucide-react";
import { tiers } from "Constants";

export const DeadlineCountdown = ({ deadlines, userTier = tiers.STARTER }) => {
  const [selectedTimezone, setSelectedTimezone] = useState("PST");
  const [currentTime, setCurrentTime] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    setCurrentTime(new Date());

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getTimezoneTime = (timezone) => {
    if (!currentTime || !isClient) {
      return "--:--:--";
    }

    const options = {
      timeZone:
        timezone === "IST"
          ? "Asia/Kolkata"
          : timezone === "PST"
          ? "America/Los_Angeles"
          : "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    return currentTime.toLocaleTimeString("en-US", options);
  };

  const getDeadlineTypeColor = (type) => {
    switch (type) {
      case "early":
        return "text-red-600 bg-red-50";
      case "regular":
        return "text-blue-600 bg-blue-50";
      case "scholarship":
        return "text-green-600 bg-green-50";
      case "visa":
        return "text-purple-600 bg-purple-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="flex w-full rounded-xl p-4 flex-col mb-4 shadow-lg">
      <div className="flex items-center justify-between mb-4 w-full">
        <div className="flex items-center space-x-2">
          <Clock className="w-6 h-6 text-[#80e280]" />
          <h3 className="text-xl font-bold text-gray-900 mb-0">
            🌐 Global Clock & Deadlines
          </h3>
        </div>
      </div>

      {/* Horizontal Layout */}
      <div className="flex flex-wrap w-full gap-4 h-fit">
        {/* Left: Timezone & Clock */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 m-2 flex-1 min-w-[300px] min-h-[200px]">
          <div className="flex items-center space-x-2 mb-3">
            <Globe className="w-5 h-5 text-[#80e280]" />
            <h4 className="font-semibold text-gray-900">Current Time</h4>
          </div>

          <div className="space-y-3">
            <div className="flex space-x-2">
              {["PST", "EST", "IST"].map((tz) => (
                <div
                  key={tz}
                  onClick={() => setSelectedTimezone(tz)}
                  className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                    selectedTimezone === tz
                      ? "bg-[#80e280] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  } hover:cursor-pointer`}
                >
                  {tz}
                </div>
              ))}
            </div>
            <div className="text-2xl font-mono text-gray-700 text-center">
              {isClient && currentTime
                ? getTimezoneTime(selectedTimezone)
                : "--:--:--"}
            </div>
          </div>
        </div>

        {/* Right: All Upcoming Deadlines */}
        <div className="space-y-3 relative flex-1 m-2 p-4 min-w-[300px] min-h-[200px]">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900 text-base">
              All Upcoming Deadlines
            </h4>
            {userTier === tiers.STARTER && (
              <Crown className="w-5 h-5 text-yellow-500" />
            )}
          </div>

          <div className="relative">
            {deadlines.slice(0, 3).map((deadline, index) => (
              <motion.div
                key={deadline.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 1.2 + index * 0.1 }}
                className={`p-3 bg-gray-50 rounded-lg ${
                  userTier === tiers.STARTER ? "blur-sm" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-900 text-sm">
                    {deadline.name}
                  </span>
                  <span className="text-sm font-bold text-gray-700">
                    {deadline.daysLeft}d
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{deadline.date}</span>
                  <span
                    className={`px-1 py-0.5 rounded text-sm font-medium ${getDeadlineTypeColor(
                      deadline.type
                    )}`}
                  >
                    {deadline.type}
                  </span>
                </div>
              </motion.div>
            ))}

            {/* Premium overlay for deadlines list - only for starter users */}
            {userTier === tiers.STARTER && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/90 rounded-lg">
                <div className="text-center">
                  <Crown className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-base font-semibold text-gray-700">
                    Premium Feature
                  </p>
                  <p className="text-sm text-gray-500">
                    Upgrade to view all deadlines
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
